/**
 * Chat image lightbox
 *
 * Same PhotoSwipe the feed uses (see controllers/photoswipe_gallery_controller.js),
 * driven programmatically instead of from markup: chat images live behind
 * authenticated mxc:// URLs, so there is no href a declarative gallery could
 * point at. The gallery is collected from the timeline at click time, so
 * swiping moves through every image in the room, not just the one tapped.
 */
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";
import { loadFullMedia, downloadAuthenticatedFile } from "./media";

const IMAGE_SELECTOR = "img[data-mxc-url]";

let lightbox = null;

/**
 * Make a rendered chat image open in the lightbox on click.
 * `content` is the m.image event content.
 */
export function attachImageLightbox(imgEl, content, fallbackLabel = "Bild") {
  imgEl.dataset.mxcUrl = content.url;
  imgEl.dataset.filename = content.filename || content.body || fallbackLabel;
  if (content.info?.mimetype) imgEl.dataset.mimetype = content.info.mimetype;
  // Dimensions from the event, not from the thumbnail the timeline shows —
  // PhotoSwipe sizes its zoom against the original.
  if (content.info?.w) imgEl.dataset.width = content.info.w;
  if (content.info?.h) imgEl.dataset.height = content.info.h;

  imgEl.classList.add("bryght-message-image--zoomable");
  imgEl.addEventListener("click", (e) => {
    // A long-press opened the context menu over this image; the click that
    // trails it is not a request to zoom.
    if (document.querySelector(".bryght-context-overlay")) return;
    e.preventDefault();
    openLightbox(imgEl);
  });
}

function openLightbox(imgEl) {
  // The gallery in the chat details swipes through its own images, the
  // timeline through the room's.
  const scope = imgEl.closest("[data-lightbox-scope]") || imgEl.closest("#bryght-timeline") || document;
  const images = [...scope.querySelectorAll(IMAGE_SELECTOR)].filter((el) => el.src);
  const index = images.indexOf(imgEl);
  if (index === -1) return;

  const items = images.map((el) => ({
    // Opens on the thumbnail that is already decoded, then swaps in the
    // original once it arrives — no blank slide while a few MB download.
    src: el.currentSrc || el.src,
    width: Number(el.dataset.width) || el.naturalWidth || 1200,
    height: Number(el.dataset.height) || el.naturalHeight || 900,
    alt: el.alt,
    element: el,
    mxcUrl: el.dataset.mxcUrl,
    mimetype: el.dataset.mimetype || null,
    filename: el.dataset.filename || "download",
  }));

  getLightbox().loadAndOpen(index, items);
}

function getLightbox() {
  if (lightbox) return lightbox;

  lightbox = new PhotoSwipeLightbox({
    pswpModule: () => import("photoswipe"),
    bgOpacity: 0.9,
    bgClickAction: "close",
    tapAction: "close",
    paddingFn: (viewportSize) => {
      if (viewportSize.x > 800) {
        return { top: 80, bottom: 80, left: 100, right: 100 };
      }
      return { top: 20, bottom: 80, left: 0, right: 0 };
    },
  });

  lightbox.on("afterInit", () => loadOriginal(lightbox.pswp));
  lightbox.on("change", () => loadOriginal(lightbox.pswp));
  registerDownloadButton(lightbox);
  lightbox.init();

  return lightbox;
}

// Replaces the current slide's thumbnail with the full-size original.
async function loadOriginal(pswp) {
  const index = pswp?.currIndex;
  const item = pswp?.options?.dataSource?.[index];
  // The flag is set before awaiting: `change` fires again from
  // refreshSlideContent below, and a second pass would loop.
  if (!item || item.loadedOriginal || !item.mxcUrl) return;
  item.loadedOriginal = true;

  const src = await loadFullMedia(item.mxcUrl, item.mimetype);
  if (!src || src === item.src || window.pswp !== pswp) return;

  const size = await measure(src);
  item.src = src;
  if (size) {
    item.width = size.width;
    item.height = size.height;
  }

  if (window.pswp === pswp) pswp.refreshSlideContent(index);
}

// The event's info.w/h is optional and the thumbnail's is wrong, so the
// original's own dimensions are the only reliable ones.
function measure(src) {
  return new Promise((resolve) => {
    const probe = new Image();
    probe.onload = () => resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
    probe.onerror = () => resolve(null);
    probe.src = src;
  });
}

function registerDownloadButton(instance) {
  instance.on("uiRegister", () => {
    instance.pswp.ui.registerElement({
      name: "download-button",
      order: 8,
      isButton: true,
      html: {
        isCustomSVG: true,
        inner:
          '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 15.7l6 6.3 6-6.3-1.5-1.4ZM23 23H9v2h14" id="pswp__icn-download"/>',
        outlineID: "pswp__icn-download",
      },
      onInit: (el, pswp) => {
        el.addEventListener("click", (e) => {
          e.preventDefault();
          const data = pswp.currSlide?.data;
          if (!data?.mxcUrl) return;
          downloadAuthenticatedFile(data.mxcUrl, data.filename || "download");
        });
      },
    });
  });
}
